import { useState } from 'react';
import { useLocation } from 'wouter';
import { useCreateAppointment } from '@/hooks/useScheduling';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { SCHEDULING_PERMISSIONS, hasPermission } from '@/lib/schedulingPermissions';
import {
  AssociationLookupSelector,
  type SelectedAssociation,
} from '@/components/business/AssociationLookupSelector';

export default function AppointmentCreatePage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { createAppointment, loading, error } = useCreateAppointment();

  const [customer, setCustomer] = useState<SelectedAssociation | null>(null);
  const [assignedStaff, setAssignedStaff] = useState<SelectedAssociation | null>(null);
  const [form, setForm] = useState({
    serviceType: 'consultation',
    startAt: '',
    endAt: '',
    notes: '',
  });

  // Permission check — after all hooks
  const canCreate = hasPermission(permissions, SCHEDULING_PERMISSIONS.APPOINTMENT_CREATE);
  if (!canCreate) {
    return <AccessDenied resource="新建预约" />;
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customer?.id || !form.startAt || !form.endAt || !assignedStaff?.id) {
      return;
    }
    const result = await createAppointment({
      customerId: customer.id,
      serviceType: form.serviceType,
      // Convert datetime-local value to ISO 8601 string — no forbidden type casts
      startAt: new Date(form.startAt).toISOString(),
      endAt: new Date(form.endAt).toISOString(),
      assignedStaffId: assignedStaff.id,
      notes: form.notes || undefined,
    });
    if (result) {
      navigate('/scheduling/appointments');
    }
  }

  return (
    <div className="max-w-xl space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/scheduling/appointments')}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← 返回列表
        </button>
        <h1 className="text-2xl font-semibold">新建预约</h1>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          创建失败：{error.message}
        </div>
      )}

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            客户 <span className="text-destructive">*</span>
          </label>
          <AssociationLookupSelector
            kind="customer"
            value={customer}
            onChange={setCustomer}
            placeholder="按姓名搜索客户…"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            服务类型 <span className="text-destructive">*</span>
          </label>
          <select
            name="serviceType"
            value={form.serviceType}
            onChange={handleChange}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="consultation">咨询</option>
            <option value="infusion">输液</option>
            <option value="followup">随访</option>
            <option value="assessment">评估</option>
            <option value="other">其他</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            开始时间 <span className="text-destructive">*</span>
          </label>
          <input
            type="datetime-local"
            name="startAt"
            value={form.startAt}
            onChange={handleChange}
            required
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            结束时间 <span className="text-destructive">*</span>
          </label>
          <input
            type="datetime-local"
            name="endAt"
            value={form.endAt}
            onChange={handleChange}
            required
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            负责员工 <span className="text-destructive">*</span>
          </label>
          {/*
            appointments.assigned_staff_id is wired through services that
            compare assignment scopes against system user IDs (see Codex
            #344 audit). Use kind="user" so the form persists users.id;
            kind="employee" would write an hr_employees.id and orphan
            the appointment from user-scoped queries. Issue #344 P1 fix.
          */}
          <AssociationLookupSelector
            kind="user"
            value={assignedStaff}
            onChange={setAssignedStaff}
            placeholder="搜索员工姓名…"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">备注</label>
          <textarea
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={3}
            placeholder="可选备注信息"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? '提交中...' : '创建预约'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/scheduling/appointments')}
            className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            取消
          </button>
        </div>
      </form>
    </div>
  );
}
